import { Divider } from "@mui/material";
import TextSelect from "components/TextSelect";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import {
  selectAdminTimeWhen,
  setAdminTimeInterval,
  TimeWhen,
  selectAdminBackPeriod,
  selectAdminTimeUnit,
  fetchSbrCaseStats,
  selectSBRAdminCaseStats,
  selectReviewerCaseStats,
} from "redux/slices/clientLens";
import { selectActiveFirmIdN } from "redux/slices/client";
import { SBRPendingReviews } from "routes/lenses/lens/admin/PendingReviews";
import CompletedReviews from "routes/lenses/lens/admin/CompletedReviews";
import { SBRCompletedReviewsTimeline } from "routes/lenses/lens/admin/CompletedReviewsTimeline";
import LensAdminReports from "routes/lenses/lens/admin/LensAdminReports";
import LensAdminManagement from "routes/lenses/lens/admin/management/Management";
import { HeaderCard, HeaderCardTitle } from "components/HeaderCard";
import { SBRProgress } from "SBR/SBRProgress";
import { selectOutcomeEnabledFormSections } from "redux/slices/form.slice";
import { useLensStats } from "helpers/hooks/useLensStats/useLensStats";

const options = [
  {
    label: "Timeframe: This Week",
    value: TimeWhen.ThisWeek,
  },
  {
    label: "Timeframe: Last Week",
    value: TimeWhen.LastWeek,
  },
  {
    label: "Timeframe: This Month",
    value: TimeWhen.ThisMonth,
  },
  {
    label: "Timeframe: Last Month",
    value: TimeWhen.LastMonth,
  },
  {
    label: "Timeframe: This Year",
    value: TimeWhen.ThisYear,
  },
  {
    label: "Timeframe: Last Year",
    value: TimeWhen.LastYear,
  },
];

interface LensAdminProps {
  lensId: number;
}

export function SBRLensAdmin(props: LensAdminProps) {
  const { lensId } = props;
  const [completedChart] = useState<"individual" | "team">("team");
  const outcomeEnabledFormSections = useAppSelector(selectOutcomeEnabledFormSections);
  const timeframe = useAppSelector(selectAdminTimeWhen);
  const activeFirmId = useAppSelector(selectActiveFirmIdN);
  const dispatch = useAppDispatch();
  const sbrCaseStats = useAppSelector(selectSBRAdminCaseStats);
  const lensStats = useLensStats({ adminStats: sbrCaseStats });
  const caseStats = useAppSelector(selectReviewerCaseStats);
  const backPeriod = useAppSelector(selectAdminBackPeriod);
  const unit = useAppSelector(selectAdminTimeUnit);
  const [statsType, setStatsType] = useState(-1);
  const pendingReviewsTitle = `${lensStats.caseProgressByReviewerTotal} Pending Reviews`;

  useEffect(() => {
    dispatch(
      fetchSbrCaseStats({
        lensId,
        sectionId: statsType === -1 ? undefined : statsType,
        assigneeId: undefined,
        firmId: activeFirmId,
        unit,
        backPeriod,
      })
    );
  }, [lensId, activeFirmId, unit, backPeriod, statsType]);

  const updateWhen = (timeWhen: TimeWhen) => {
    dispatch(setAdminTimeInterval({ timeWhen }));
  };

  return (
    <>
      <div className="flex gap-2 items-center text-sm font-semibold border-t border-gray-100 py-4">
        <TextSelect
          value={statsType}
          onChange={(evt) => setStatsType(evt.target.value as number)}
          options={[
            {
              label: "View: Case-Level",
              value: -1,
            },
            ...outcomeEnabledFormSections.map((section) => ({
              label: `View: ${section.name}`,
              value: section.id,
            })),
          ]}
        />
        <Divider orientation="vertical" />
        <TextSelect value={timeframe} onChange={(event) => updateWhen(event.target.value)} options={options} />
      </div>
      <div className="grid grid-cols-2 gap-4 pb-4">
        <div className="flex flex-col gap-4">
          <HeaderCard title={<HeaderCardTitle title="Section Review Progress" />}>
            <div className="p-4 py-6">
              <SBRProgress
                activeCount={caseStats.activeCount}
                inactiveCount={caseStats.inactiveCount}
                lensId={lensId}
                formSections={outcomeEnabledFormSections}
                sectionStats={lensStats.caseProgressBySectionStats}
                activeSectionId={statsType === -1 ? undefined : statsType}
                summaryStats={{
                  completeCount: lensStats.overallCompletedCases,
                  validationCount: lensStats.overallValidationCases,
                  reviewCount: lensStats.overallReviewCases,
                }}
              />
            </div>
          </HeaderCard>

          <HeaderCard title={<HeaderCardTitle title="Reports" />}>
            <div className="min-h-0 h-full overflow-auto px-4">
              <LensAdminReports lensId={lensId} />
            </div>
          </HeaderCard>
        </div>
        <div className="flex flex-col gap-4">
          <HeaderCard title={<HeaderCardTitle title={pendingReviewsTitle} />}>
            <div className="max-h-36 h-36 px-4 p-1">
              <SBRPendingReviews lensId={lensId} stats={lensStats.caseProgressByReviewerStats} />
            </div>
          </HeaderCard>

          <HeaderCard
            title={
              <HeaderCardTitle className="flex">
                <p className="text-sm font-semibold">
                  {lensStats.completedCaseStatsTotal.toLocaleString()} Reviews Completed
                </p>
                <div className="grow" />
              </HeaderCardTitle>
            }>
            <div className="px-4 max-h-36 h-36">
              {completedChart === "individual" && <CompletedReviews lensId={lensId} height={138} />}
              {completedChart === "team" && (
                <SBRCompletedReviewsTimeline
                  unit={unit}
                  stats={lensStats.completedCaseStats}
                  height={138}
                  lensId={lensId}
                />
              )}
            </div>
          </HeaderCard>

          <HeaderCard title={<HeaderCardTitle title="Management" />}>
            <div className="p-4">
              <LensAdminManagement lensId={lensId} />
            </div>
          </HeaderCard>
        </div>
      </div>
    </>
  );
}
